/**
 * OAuth Callback URL Handling Utilities
 *
 * Provides utilities for processing OAuth callback URLs from InAppBrowser,
 * including handling platform-specific URL formatting issues and parameter extraction.
 */

export interface OAuthCallbackParams {
  code: string | null;
  state: string | null;
}

/**
 * Cleans malformed callback URLs that occur on Android
 *
 * Android InAppBrowser sometimes prefixes custom schemes with 'http://',
 * creating malformed URLs like 'http://capacitor://localhost'. This function
 * removes the invalid prefix to restore the proper custom scheme URL.
 *
 * @param url - The potentially malformed callback URL
 * @returns Cleaned URL with proper custom scheme
 */
export const cleanCallbackUrl = (url: string): string => {
  // Fix Android-specific malformed URL issue
  return url.replace('http://capacitor://', 'capacitor://');
};

/**
 * Extracts OAuth parameters from callback URL
 *
 * Parses both query parameters (?param=value) and hash parameters (#param=value)
 * to handle different OAuth response modes. Prioritizes query parameters over hash.
 *
 * @param callbackUrl - The OAuth callback URL containing auth code and state
 * @returns Object containing extracted code and state parameters
 */
export const extractOAuthParams = (callbackUrl: string): OAuthCallbackParams => {
  try {
    // Clean the URL first to handle platform-specific issues
    const cleanUrl = cleanCallbackUrl(callbackUrl);
    const url = new URL(cleanUrl);

    // Check both query parameters (?param=value) and hash parameters (#param=value)
    // This handles different OAuth response modes (query vs fragment)
    const queryParams = new URLSearchParams(url.search);
    const hashParams = new URLSearchParams(url.hash.substring(1));

    // Prioritize query parameters over hash parameters
    const code = queryParams.get('code') || hashParams.get('code');
    const state = queryParams.get('state') || hashParams.get('state');

    return { code, state };
  } catch (error) {
    console.error('[OAuth] Failed to parse callback URL:', error);
    return { code: null, state: null };
  }
};
